'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Check, ChevronDown, Globe, Building, Banknote, Landmark, Tv, Hotel } from 'lucide-react';
import { fadeInAnimationVariants } from '@/lib/animation';
import { motion } from 'framer-motion';

type CategoryType = 'all' | 'yurtdisi' | 'askeriye' | 'bankalar' | 'resmi' | 'kanallar' | 'oteller';

type ReferencesByCountry = {
  [key: string]: string[];
};

type ReferencesType = {
  askeriye: string[];
  bankalar: string[];
  resmi: string[];
  kanallar: string[];
  oteller: string[];
  yurtdisi: ReferencesByCountry;
};

const ReferanslarPage = () => {
  const [activeCategory, setActiveCategory] = useState<CategoryType>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const categories = [
    { id: 'all' as CategoryType, name: 'Tümü', icon: Check },
    { id: 'yurtdisi' as CategoryType, name: 'Yurtdışı', icon: Globe },
    { id: 'askeriye' as CategoryType, name: 'Askeri Kurumlar', icon: Building },
    { id: 'bankalar' as CategoryType, name: 'Bankalar', icon: Banknote },
    { id: 'resmi' as CategoryType, name: 'Resmi Kurumlar', icon: Landmark },
    { id: 'kanallar' as CategoryType, name: 'TV Kanalları', icon: Tv },
    { id: 'oteller' as CategoryType, name: 'Oteller', icon: Hotel },
  ];

  const references: ReferencesType = {
    askeriye: [
      'AĞRI ASKERİ GAZİNO MÜDÜRLÜĞÜ',
      'BARTIN DENİZ KOMUTANLIĞI',
      'BOLU JANDARMA İL KOMUTANLIĞI',
      'ÇANAKKALE 5. KOMONDO ALAYI',
      'DİYARBAKIR ORDU EVİ MÜDÜRLÜĞÜ',
      'EDİRNE İL JANDARMA KOLTUĞU',
      'ERZURUM 9. KOLORDU KOMUTANLIĞI',
      'HAKKÂRİ İL JANDARMA OKULU',
      'ISPARTA DAĞ KOMANDO OKULU',
      'KIBRIS 39. MEKANİZE PİYADE TÜMEN KOMUTANLIĞI',
      'KIBRIS 49. PİYADE ALAYI KOMUTANLIĞI A TİPİ',
      'KIRKLARELİ 55. MEKANİZE PİYADE TUGAY KOMUTANLIĞI',
      'KUMBURGAZ ÖZEL EĞİTİM MERKEZ KOMUTANLIĞI',
      'MANİSA ALAŞEHİR EĞİTİM ALAY KOMUTANLIĞI',
      'MARDİN DARGEÇİT İLÇE JANDARMA KOMUTANLIĞI',
      'NEVŞEHİR İL JANDARMA KOMUTANLIĞI',
      'SARIKAMIŞ ASKERİ HASTANE',
      'ŞIRNAK / SİLOPİ 172. ZIRHLI TUGAY KOMUATNLIĞI',
      'ŞIRNAK İL JANDARMA KOMUTANLIK'
    ],
    bankalar: [
      'İŞ BANKASI 4 LEVENT',
      'TÜRK TELEKOM EK HİZMET BİNASI 4 LEVENT',
      'TÜRK TELEKOM MÜDÜRLÜĞÜ MECİDİYEKÖY',
      'VAKIFBANK EK HİZMET BİNASI 4 LEVENT',
      'VAKIFLAR BANKASI 4 LEVENT',
      'VAKIFLAR BANKASI MÜDÜRLÜĞÜ ÜMRANİYE',
      'YAPIKREDİ BANKACILIK ÜSSÜ ŞEKERPINAR KOCAELİ',
      'YAPIKREDİ PLAZA 4 LEVET'
    ],
    resmi: [
      'ARDAHAN GÖLE KAYMAKAMLIK / FERİDE KARABACAK MESLEK LİSESİ',
      'ARNAVUTKÖY POLİS MESLEK EĞİTİM MERKEZİ',
      'BEYLERBEYİ POLİS EVİ',
      'EDİRNE POLİS EVİ',
      'GEREDE SOSYAL YARDIMLAŞMA VE DAYANIŞMA VAKFI BAŞKANLIĞI',
      'GÜMÜŞHANE ÖĞRETMEN EVİ',
      'İSTANBUL ATAŞEHİR CERRAHİ TIP MERKEZİ',
      'İSTANBUL BÜYÜK ŞEHİR BELEDİYESİ SARAÇHANE / FATİH',
      'İSTANBUL HAKİMEVİ (A.T.G.V.)',
      'KOCAELİ / ÇOLAKOĞLU KIZ TEKNİK VE MESLEK LİSESİ',
      'KULELİ ASKERİ LİSESİ',
      'SİİRT SOSYAL YARDIMLAŞMA VE DAYANIŞMA VAKFI BAŞKANLIĞI',
      'SİLİFKE M TİPİ KAPALI CEZA VE AÇIK CEZA İNFAZ KURUMU',
      'SİLİVRİ AÇIK CEZA EVİ MÜDÜRLÜĞÜ',
      'SİLİVRİ KAPALI CEZA İNFAZ KURUMU MÜDÜRLÜĞÜ',
      'SİVAS POLİS OKULU',
      'ZONGULDAK EMNİYET MÜDÜRLÜĞÜ'
    ],
    kanallar: [
      'ATV BEŞİKTAŞ',
      'ATV EKMEK TEKNESİ',
      'ATV KURTLAR VADİSİ DİZİSİ',
      'NTV MEHMET ALİ BİRAND',
      'SHOW TV BANA NE YAKIŞIR YARIŞMASI',
      'SHOW TV BUGÜN NE GİYSEM YARIŞMASI',
      'STAR TV BLANDAX GÜZELLİK YARIŞMASI'
    ],
    oteller: [
      'ABANT OTEL ANAR SPA WELLNESS & HEALTH CLUP',
      'ANTALYA AVSALLAR İNCE KUM SU OTEL',
      'ANTALYA SU SESİ OTEL',
      'BODRUM THE BLUE BOSPHORUS HOTEL',
      'BOLU ABANT PALACE OTEL',
      'EDİRNE RVS OTEL',
      'LEGACY OTTOMAN',
      'LİBYA SİERTE SEA SİDE OTEL',
      'MARMARİS GREEN NATURE',
      'MUĞLA MECİKLEYF OTEL',
      'PALMCİTY OTEL',
      'ULUDAĞ KERVAN OTEL'
    ],
    yurtdisi: {
      A: [
        'ALMANYA / ABDULLAH DAĞ / STR 36 46446',
        'ALMANYA / BURAK BAŞPINAR / OBERGASSE 43 PK 36341 LAUTERBACH',
        'ALMANYA / ENES SULCEVSKİ / GRÜNER WEG 34/B GREVEN',
        'ALMANYA / ERTUĞRUL BAKIR / LİMBURGER STR 53/A PK 65510 İDSTEİN',
        'ALMANYA / FAİK TACİ / SCHWARTAVER ALLEE 3A 23554 LÜBECK',
        'ALMANYA / FATİH ÇELTEK / OLGASTR 25 73728 ESSLİNGEN',
        'ALMANYA / GÖNÜL GEZGİN / GEİLEN KİREHENER STR 401 5134 HERZOGENRAHT',
        'ALMANYA / İHSAN CANARSLAN / ALTMARKT HAMBORN 47166 DUİSBURG',
        'ALMANYA / KADİR KESKİN / RATHAUSTRASSE 20 WEİSBADEN 65203',
        'ALMANYA / MEHMET BEKTİK / BOLONGARE STR 102 65929 FRANKFURT',
        'ALMANYA / MURAT NAZLI / SOLİHGERTRABE 50 40764 LEGENFELD',
        'ALMANYA / OLGUN APAYDIN / OESTERHOLZ STR 39 44145 DORTMUND',
        'ALMANYA / SEZER DOYURAN / LANGENFELD WEBER STRASSE 1 A 40764',
        'ALMANYA / YAVUZ AKBAŞ / HEER STR 175 PK 56154 BOPPARD',
        'ALMANYA / YUSUF MAKRAM / BAHNDOF STR 48 21614 BUXTEHUDE',
        'AVUSTURALYA / ERDAL YILDIZ / 3/17 PİNNAROO CCT MEADOW HEIGHTS MELBORN',
        'AVUSTURALYA / GAZİ ŞENER / 14 DARGİE CRT DALLA 3047',
        'AVUSTURALYA / ORHAN ÜNAL / LEONART PLAZ 2 6800 FELDKİCH',
        'AVUSTURALYA / OSMAN ŞİRİN / BRUNNENGASSE 68/5 1160 WİEN / WİYANA',
        'AVUSTURALYA / RAMİ ALRAHMOUN / NİKOLAUS GASSNER STRABE 41 D 5710 SALZBURG',
        'AVUSTURYA / AHMET ERDOĞAN / KLASTERNEUSBURGER STRABE 46 1200 WİAN VİYANA',
        'AVUSTURYA / SERKAN DOĞAN / BRÜNNERSTRASSE 121 A1210 WİEN VİYANA',
        'AVUSTURYA / TARIK YASEMİN / SCHWEİGLER STR 45/12 1150 WİEN VİYANA',
        'AZERBEYCAN / ZAUR ABDULLAYEV BAKÜ'
      ],
      B: [
        'BELÇİKA / AHMET ALTTEN / RUE COUİLLET 4 6001 MARCİNELLE',
        'BELÇİKA / GÖKHAN ÇEKİÇ / ANTWERPSESTEENWEG 26 PK 9000',
        'BELÇİKA / OKAN YILDIZ / 2400 MOL GİNDERBROEK 52',
        'BELÇİKA / SERHAN ALTIKAT / RUE LİNTHOUT 232 1040 ETTERBEEK',
        'BULGARİSTAN / GÖKHAN / DOBLİÇ BÖLGESİ KALARTSİ 6 NO CD. NO:4 STİLİSTRA',
        'BULGARİSTAN / İBRAHİM FAİK SMAİL / SUMEN PLOVDİV',
        'BULGARİSTAN / MUSTAFA AHMET / RAZGART',
        'BULGARİSTAN / SPAS SPASOV / ROSE 7004',
        'BULGARİSTAN / TURAN KARAKURT / UI SLATİMS JK HARİSTO SİMİRNENSKİ BLOK 45 SOFYA',
        'BULGARİSTAN / VASVİ HASANO / SOK G SKRİJOVSKİ 5 SVLENGRAD'
      ],
      D: [
        'DUBAİ / ESSA ALI JUMA / MİZHAR'
      ],
      F: [
        'FİNLANDİYA / JAFF PARTURİ / KOMPAANA KAİVONKATU 12 00100 HELSİNKİ',
        'FRANSA / HAMZA BÜYÜKOĞLU / 6 RUE EUGENE BOUDİN 56000 / VANNES',
        'FRANSA / İBRAHİM KESTİRİCİ / 17 ROUTE DE STRASBOURG 67500 / HAGUENAU',
        'FRANSA / MEHMET / 37 RUE JEAN JAURE 60100 / CREİL',
        'FRANSA / MEHMET ERGÜN / 27 RUE LA CAVEE DE CREİL 60250 / BURY',
        'FRANSA / SAS ASLAN / 270 RUE FRANCONİS FRANDAZ 38290 / LA VORPILTİERE',
        'FRANSA / SAS LA PLACE 23 / 11 RUE HENRİ DUNANT 60100 / CREİL'
      ],
      G: [
        'GÜRCİSTAN / HABİB GERGİA / ANGİSA MH NO:2 / BATUM',
        'GÜRCİSTAN / MEHMET EKER / HULO CD. NO:9 / BATUM'
      ],
      H: [
        'HOLLANDA / AHMET BAŞGÖZE / VLEESSTRAAR 13 4001 BB / TİEL',
        'HOLLANDA / DARUN SAMIZ / WİLLEMSWEG 42 6531 DM / NİJMEGEN',
        'HOLLANDA / FATİH EROĞLU / KAPSALON RE STYLE MAANDERİND 28 6711 / NEDERLAND',
        'HOLLANDA / FİLİZ BİLİR / DUKAATPASSAGE 19 8232 GC / LELYSTAD',
        'HOLLANDA / HÜSEYİN SAY / CASTOR 79 3902 SE / VEENENDAAL',
        'HOLLANDA / RIDVAN CANDAN / KROMMESTRAAT 2 3771HB / BARNEVELD',
        'HOLLANDA / ŞEMSİ YAVUZGİL / CORROUSELWEG 9 3225LN / HELLEVOETSLUİS'
      ],
      I: [
        'IRAK / ALDAVER UNİTED CO LTD / DİNAR STREER JUBAİLA / BASRA',
        'IRAK / DILOVAN KUAÖR / DUHOLE',
        'IRAK / OSMANNURİ KARATAŞ / BAHDİYARİ KAVN CD. FORSTİ İŞARATRAFİK / SÜLEYMANİYE',
        'IRAK / RASHA ABBAS NUHAR / HAVUZU SK. TİME SQUİR JUBAİLA / BASRA'
      ],
      İ: [
        'İNGİLTERE / BİLAL TEKAĞAÇ / 177 A ASH ROAD GU12 4DB',
        'İNGİLTERE / BÜLENT TEPE / 70 WELLİNGTON STREER BALLY MENA BT43 6AD UK',
        'İNGİLTERE / BÜŞRA TORBAŞ / 1. NELSON ROAD NORTGREAT YARMOUHT NR 302 AS',
        'İNGİLTERE / ERDOĞAN KOŞKOŞ / 3 ALDERGATE TANWARTH B797DJ / STAFFORDSHİRE',
        'İNGİLTERE / HAKAN İKİNCİSOY / 88 SHEEP STREET BICESTEROV26 6LP OVFORD SRİRE',
        'İNGİLTERE / HEVRE / 171 WELLİNGBOROGH ROAD NORTHAMPTON NN1 4DX UK',
        'İNGİLTERE / MÜSLİM AHMED MOHAMMED / ROAD WOLWERHAMPTON 285 TETTENHALL WV6 OLE',
        'İNGİLTERE / SABİNA KALANTAROVA / UNİT4.COM EVCHANGE ALBERT STREER DARBY',
        'İNGİLTERE / SÜLEYMAN YALÇIN / 124 HİGH STREET CHESHAM HP 5 1EB',
        'İNGİLTERE / ŞIH MEHMET YILMAZ / 126 GLOUCESTER AVENUE C112 9 LG',
        'İNGİLTERE / ŞENOL NACAR / 27 BLUE STREER CARMARTHEN SA31 3LE SOUTH WALES / KİNGDOM',
        'İSKOÇYA / BÜLENT DİNGİL / 32 NEW STREER DALRY AYRISHIRE KA24 5AG / KİNGDOM',
        'İSKOÇYA / MASTERCUBTS TURKISH BARBER / 89 HOLBURN ST ABERDEEN PK AB10 6 BQ / SCOTLAND',
        'İSKOÇYA / NİHAT BOSTON / SCOTLAND ABERDEEN AB 11 6 HB 16 CROWN STREET',
        'İSVİÇRE / MEHMET KILIÇ / RUEDE 21 1020 / RENENS',
        'İSVİÇRE / MURAT DOYMAZ / ZURİCHER STRASSE 5 4332'
      ],
      K: [
        'KATAR / MONERA YOUSEF EMAL BALOSHI / CLTİY AZAZİA PK 8244 / DOHA',
        'KAZAKİSTAN / AMANGALİ VA / THE REPUBİC OF KAZAKİSTAN ATYRAU CİTY STREET VLADİMİRSKİ 8',
        'KKTC / EYPS LTD / 64 MEHMET AKİF CD PLUMER APT NO:1 / LEFKOŞA',
        'KKTC / GÜNNASİR ERBEKÇİ / ERTUĞRUL KUYUMCU OĞLU SK. KAYRENYA APT 2 / GİRNE',
        'KKTC / HALİL HASAN RUSO / KARALAR SK. ALANİCİ GAZİMOGA',
        'KOSOVA / GENÇ SYLA / İPEK YOL ADEM JASHAR'
      ],
      M: [
        'MALTA / VOLKAN SİNOP / ST ANDREW STR 17 SWİEQİ',
        'MOSKOVA / ORHAN KAYA'
      ],
      S: [
        'SUUDİ ARABİSTAN / MEHMET BEY / HUFUF'
      ],
      U: [
        'UKRAYNA / ÖZBEY SEVİNDİK / KIEV'
      ],
      Y: [
        'YUNANİSTAN / RAMADAN NİZAM / STARTİNG 13 KSANTHİ'
      ]
    }
  };

  const getFilteredReferences = () => {
    if (activeCategory === 'all') {
      // Tüm kategorileri birleştirerek göster
      const allReferences: { letter: string; references: string[] }[] = [];
      
      // Yurtdışı harici kategorileri ekle
      ['askeriye', 'bankalar', 'resmi', 'kanallar', 'oteller'].forEach(category => {
        const categoryName = categories.find(cat => cat.id === category)?.name || '';
        allReferences.push({
          letter: categoryName,
          references: references[category as keyof Omit<ReferencesType, 'yurtdisi'>] || []
        });
      });
      
      // Yurtdışı referanslarını ekle
      Object.entries(references.yurtdisi).forEach(([letter, refs]) => {
        allReferences.push({
          letter: `Yurtdışı - ${letter}`,
        references: refs
        });
      });
      
      return allReferences;
    } else if (activeCategory === 'yurtdisi') {
      return Object.entries(references.yurtdisi).map(([letter, refs]) => ({
        letter: letter,
        references: refs
      }));
    } else {
      return [{
        letter: categories.find(cat => cat.id === activeCategory)?.name || '',
        references: references[activeCategory] || []
      }];
    }
  };

  // Aktif kategoriyi bulma fonksiyonu
  const getActiveCategory = () => {
    return categories.find(cat => cat.id === activeCategory) || categories[0];
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="container mx-auto px-4">
        <motion.h1 
          className="text-3xl font-bold text-center mb-8 relative"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Referanslarımız
          <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-primary mt-2"></span>
        </motion.h1>
        
        {/* Kategoriler için Açılır Menü (Mobil) */}
        {isMobile && (
          <div className="relative mb-6">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <div className="flex items-center">
                {(() => {
                  const activeCategory = getActiveCategory();
                  if (activeCategory.icon) {
                    const Icon = activeCategory.icon;
                    return <Icon className="h-5 w-5 mr-2 text-primary" />;
                  }
                  return null;
                })()}
                <span>{getActiveCategory().name}</span>
              </div>
              <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`} />
            </button>
            
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      activeCategory === category.id ? 'bg-gray-50 text-primary font-medium' : ''
                    }`}
                  >
                    {(() => {
                      if (category.icon) {
                        const Icon = category.icon;
                        return <Icon className="h-5 w-5 mr-2" />;
                      }
                      return null;
                    })()}
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Kategoriler için Tab Menü (Masaüstü) */}
        {!isMobile && (
          <div className="flex flex-wrap items-center justify-center mb-8 gap-2">
            {categories.map((category, index) => (
              <motion.button
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  activeCategory === category.id
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {(() => {
                  if (category.icon) {
                    const Icon = category.icon;
                    return <Icon className="h-4 w-4 mr-1.5" />;
                  }
                  return null;
                })()}
                {category.name}
              </motion.button>
            ))}
          </div>
        )}

        {/* Referanslar Listesi */}
        <div className="max-w-5xl mx-auto">
          {getFilteredReferences().map(({ letter, references }, groupIndex) => (
            <motion.div 
              key={`${letter}-${groupIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
              className="mb-6"
            >
              <div className="flex items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-800">{letter}</h2>
                <div className="ml-3 flex-grow h-px bg-gray-200"></div>
                <span className="text-sm text-gray-500 ml-2">{references.length} referans</span>
        </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {references.map((ref: string, index: number) => (
                  <motion.div 
                    key={index}
                    variants={fadeInAnimationVariants}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    custom={index}
                    className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all hover:scale-[1.01] text-sm text-gray-700 border border-gray-100"
                  >
                    {ref}
                  </motion.div>
                ))}
            </div>
            </motion.div>
          ))}
        </div>
        
        {/* Özet Bilgi Kutusu */}
        <motion.div 
          className="mt-12 bg-white p-5 rounded-xl shadow-sm max-w-3xl mx-auto border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Çalışmalarımız ve Referanslarımız</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            Filiz Kozmetik olarak, Türkiye genelinde ve dünya çapında birçok prestijli kurum, işletme ve işyeri ile çalışma imkanı bulduk. 
            Müşteri memnuniyetini her zaman ön planda tutarak, kaliteli hizmet anlayışımızla hem yurt içinde hem de yurt dışında tercih edilen bir marka haline geldik.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ReferanslarPage;